/**
 * Sign-in routing: which form, AccessType and SkinType the current URL gets.
 *
 * Backend model (see CLAUDE.md "Authentication & access model"): one AuthenticationController
 * (login, secure-login, passkey challenge/verify, refreshtoken, logout) and one ServicesController
 * (sync, account, content, data, notify, log, mkt) — there is NO per-route URL prefix, so every
 * route below calls the same /api/Authentication/* and /api/services/* endpoints and differs only
 * in the form shown and the AccessType/SkinType sent. Once signed in, the JWT carries the
 * AccessType and user id the backend works from.
 *
 *   User-based  (login endpoints; unique email + name + AccessType within a SkinType)
 *     /                                  standard form   AccessType 10   SkinType 3, or the channel skin
 *     /psychscan /childnav /patienttools
 *     /comt /carengrow                   standard form   AccessType 10   SkinType 1 / 2 / 3 / 5 / 6
 *     /channel                           admin form      AccessType 20   SkinType 3
 *     /account (future)                  admin form      AccessType 21   SkinType 3
 *     /office                            standard form   AccessType 30   SkinType 3
 *   Token-based (secure-login endpoint; unique token within an AccessType, skin set by the token)
 *     /secure/{token} 40   /link/{token} 41   /remote/{token} 42   /share/{token} 43
 */

export type AuthEntryMode = 'pro' | 'channel' | 'account' | 'office';
export type TokenEntryMode = 'remote' | 'secure' | 'link' | 'share';

/** AccessType credentialing/backend-service value for each token-based mode. */
export const TOKEN_ACCESS_TYPE: Record<TokenEntryMode, string> = {
  secure: '40',
  link: '41',
  remote: '42',
  share: '43',
};

export interface TokenRouteMatch {
  mode: TokenEntryMode;
  token: string;
  routePath: string;
}

export interface AuthEntryConfig {
  mode: AuthEntryMode;
  routePath: string;
  /**
   * 'standard' = email + name + verification code (+ passkey / sign-up where enabled).
   * 'admin'    = email + name + password + verification code.
   */
  form: 'standard' | 'admin';
  accessType: '10' | '20' | '21' | '30';
  /** Admin forms always sign in with this SkinType (the skin routes/host overrides don't apply). */
  fixedSkinType?: string;
  requiresPassword: boolean;
  requiresTwoFactorCode: boolean;
  passkeyEnabled: boolean;
  /** Heading + helper text of the admin form. */
  title?: string;
  subtitle?: string;
  /** Extra text shown above the form's fields. */
  policyNotice?: string;
}

/** Default SkinType ("Patient Tools") for every route that doesn't pick another skin. */
export const DEFAULT_SKIN_TYPE = '3';

const AUTH_ENTRY_CONFIGS: Record<AuthEntryMode, AuthEntryConfig> = {
  pro: {
    mode: 'pro',
    routePath: '/pro',
    form: 'standard',
    accessType: '10',
    requiresPassword: false,
    requiresTwoFactorCode: true,
    passkeyEnabled: true,
  },
  channel: {
    mode: 'channel',
    routePath: '/channel',
    form: 'admin',
    accessType: '20',
    fixedSkinType: DEFAULT_SKIN_TYPE,
    requiresPassword: true,
    requiresTwoFactorCode: true,
    passkeyEnabled: false,
    title: 'Channel Administration Sign In',
    subtitle: 'Use your channel administration credentials.',
  },
  // Account-level administrators (future work): same form as /channel, AccessType 21.
  account: {
    mode: 'account',
    routePath: '/account',
    form: 'admin',
    accessType: '21',
    fixedSkinType: DEFAULT_SKIN_TYPE,
    requiresPassword: true,
    requiresTwoFactorCode: true,
    passkeyEnabled: false,
    title: 'Account Administration Sign In',
    subtitle: 'Use your account administration credentials.',
  },
  // Office users sign in on the standard form (AccessType 30) with an added line of text.
  office: {
    mode: 'office',
    routePath: '/office',
    form: 'standard',
    accessType: '30',
    requiresPassword: false,
    requiresTwoFactorCode: true,
    passkeyEnabled: true,
    policyNotice: 'Sign in with your office account credentials.',
  },
};

/**
 * Which sign-in route the path selects. Matches the whole first path segment (/channel,
 * /channel/..., not /channelfoo); anything else — including the skin routes — is 'pro'.
 */
export const resolveAuthEntryModeFromPath = (pathName: string): AuthEntryMode => {
  const firstSegment = pathName.trim().toLowerCase().split(/[/?#]/).filter(Boolean)[0] ?? '';
  switch (firstSegment) {
    case 'channel':
      return 'channel';
    case 'account':
      return 'account';
    case 'office':
      return 'office';
    default:
      return 'pro';
  }
};

/** Branded sign-in routes (/psychscan, ...) and the SkinType each selects. */
const SKIN_TYPE_BY_ROUTE: Record<string, string> = {
  psychscan:   '1',
  childnav:    '2',
  patienttools: '3',
  comt:        '5',
  carengrow:   '6',
};

/**
 * Fallbacks when the path isn't a branded route: a page URL (host + path) merely CONTAINING one of
 * these names (e.g. psychscan.example.com) defaults to that skin, same as using /<name>.
 * Checked in order, after the exact branded routes, before the Patient Tools default.
 */
const SKIN_TYPE_BY_URL_TEXT: Array<[text: string, skinType: string]> = [
  ['psychscan', '1'],
  ['childnav', '2'],
  ['patienttools', '3'],
  ['comt', '5'],
  ['carengrow', '6'],
];

/**
 * Sign-in SkinType for the current page. Priority: a branded route path (/psychscan, /childnav,
 * /patienttools, /comt, /carengrow), then the page URL (host + path) containing one of those
 * names, then the Patient Tools default. Admin routes (/channel, /account) pass their
 * `mode` and always get their fixed SkinType; token routes take their skin from the token.
 */
export const resolveSkinTypeFromPath = (
  pathName: string,
  hostName = '',
  mode: AuthEntryMode = resolveAuthEntryModeFromPath(pathName)
): string => {
  const fixed = AUTH_ENTRY_CONFIGS[mode].fixedSkinType;
  if (fixed) {
    return fixed;
  }

  const normalized = pathName.trim().toLowerCase();
  const route = Object.keys(SKIN_TYPE_BY_ROUTE).find((r) => normalized.startsWith(`/${r}`));
  if (route) {
    return SKIN_TYPE_BY_ROUTE[route];
  }

  const urlText = `${hostName.trim().toLowerCase()}${normalized}`;
  const byUrlText = SKIN_TYPE_BY_URL_TEXT.find(([text]) => urlText.includes(text));
  return byUrlText ? byUrlText[1] : DEFAULT_SKIN_TYPE;
};

export const resolveTokenRoute = (pathName: string): TokenRouteMatch | null => {
  const normalized = pathName.trim();
  const match = normalized.match(/^\/(remote|secure|link|share)\/([^/?#]+)/i);
  if (!match) {
    return null;
  }

  const mode = match[1].toLowerCase() as TokenEntryMode;
  const token = decodeURIComponent(match[2]);
  return {
    mode,
    token,
    routePath: `/${mode}/${match[2]}`,
  };
};

/**
 * Whether a session stored in this browser (its AccessType from `auth_access_type`) may be used
 * on this route. The base/skin routes ('pro') accept whatever is stored; the dedicated routes
 * (/channel, /account, /office) only accept a session of their own AccessType — any
 * other stored session is signed out and that route's sign-in form is shown.
 */
export const routeAcceptsStoredAccessType = (
  config: AuthEntryConfig,
  storedAccessType: string | null | undefined
): boolean => {
  if (config.mode === 'pro') {
    return true;
  }
  return (storedAccessType ?? '').trim() === config.accessType;
};

export const getAuthEntryConfig = (mode: AuthEntryMode): AuthEntryConfig => AUTH_ENTRY_CONFIGS[mode];

export const getDefaultAuthRoute = (): string => AUTH_ENTRY_CONFIGS.pro.routePath;
